/**
 * Canonical Field Ontology for Permit Forms
 *
 * This ontology maps common permit field synonyms to standardized field IDs.
 * Used by the AI Smart Schema pipeline to normalize extracted fields.
 *
 * Structure:
 * - fieldId: The canonical snake_case identifier (maps to dataPath)
 * - synonyms: Common labels found on permit forms
 * - type: Data type for validation and UI rendering
 * - category: Logical grouping for wizard steps
 * - transform: Optional transform function name
 * - wizardStep: Which TurboTax-style step this belongs to
 */

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Field data types for validation and UI rendering.
 * Merged from original implementation + Phase 6 SemanticDataType.
 */
export type FieldType =
  | 'string'           // Generic text
  | 'text'             // Multiline text
  | 'person_name'      // Full legal name (from Phase 6)
  | 'company_name'     // Business/company name (from Phase 6)
  | 'number'
  | 'currency'
  | 'date'
  | 'phone'
  | 'email'
  | 'address'          // Full address
  | 'street_address'   // Street only
  | 'city'             // City only (from Phase 6)
  | 'state'            // State only (from Phase 6)
  | 'zip'              // ZIP only (from Phase 6)
  | 'boolean'
  | 'enum'
  | 'license_number'   // License/certification number (from Phase 6)
  | 'parcel_id'        // Tax parcel/folio (from Phase 6)
  | 'signature'
  | 'initials';

export type FieldCategory =
  | 'property'
  | 'owner'
  | 'contractor'
  | 'project'
  | 'permit'
  | 'surety'
  | 'lender'
  | 'dates'
  | 'notary'
  | 'signatures'
  | 'pool'
  | 'electrical'
  | 'mechanical'
  | 'plumbing'
  | 'roofing'
  | 'private_provider';

export type WizardStep =
  | 'project_site'        // Step 1: Project & Site Information
  | 'applicant_contractor' // Step 2: Applicant & Contractor
  | 'scope_permit_type'   // Step 3: Scope & Permit Type
  | 'job_details'         // Step 4: Job Details (valuation, systems)
  | 'documents'           // Step 5: Documents & Attachments
  | 'review_export';      // Step 6: Review & Export

export interface OntologyField {
  /** Canonical field ID (snake_case) - maps to dataPath */
  fieldId: string;

  /** Common labels/synonyms found on permit forms (case-insensitive matching) */
  synonyms: string[];

  /** Data type for validation and UI */
  type: FieldType;

  /** Logical category for grouping */
  category: FieldCategory;

  /** Wizard step this field belongs to */
  wizardStep: WizardStep;

  /** Optional transform function name */
  transform?: string;

  /** Default value if commonly pre-filled */
  defaultValue?: string;

  /** Whether this field is typically required */
  commonlyRequired?: boolean;

  /** Parent dataPath prefix (job, contractor, generated) */
  dataPathPrefix: 'job' | 'contractor' | 'generated';

  /** Human-readable label for UI */
  displayLabel: string;

  /** Description for tooltips/help text */
  description?: string;
}

// =============================================================================
// CANONICAL FIELD ONTOLOGY
// =============================================================================

export const FIELD_ONTOLOGY: Record<string, OntologyField> = {

  // ===========================================================================
  // PROPERTY INFORMATION
  // ===========================================================================

  'address': {
    fieldId: 'address',
    synonyms: [
      'project address', 'property address', 'site address', 'job address',
      'location', 'address of project', 'address of property',
      'address of construction', 'construction address', 'job site address',
      'street address', 'property location', 'site location'
    ],
    type: 'address',
    category: 'property',
    wizardStep: 'project_site',
    transform: 'fullAddress',
    commonlyRequired: true,
    dataPathPrefix: 'job',
    displayLabel: 'Project Address',
    description: 'Full street address where work will be performed'
  },

  'parcelNumber': {
    fieldId: 'parcelNumber',
    synonyms: [
      'parcel number', 'parcel no', 'parcel #', 'tax folio', 'tax folio no',
      'folio number', 'property id', 'pin', 'apn', 'assessor parcel number',
      're number', 'real estate number', 'tax id'
    ],
    type: 'string',
    category: 'property',
    wizardStep: 'project_site',
    dataPathPrefix: 'job',
    displayLabel: 'Parcel/Folio Number',
    description: 'Tax parcel or folio number from property records'
  },

  'legalDescription': {
    fieldId: 'legalDescription',
    synonyms: [
      'legal description', 'legal desc', 'property legal description',
      'legal description of property', 'lot block subdivision',
      'legal description of property being improved'
    ],
    type: 'text',
    category: 'property',
    wizardStep: 'project_site',
    transform: 'truncate80',
    dataPathPrefix: 'job',
    displayLabel: 'Legal Description',
    description: 'Legal property description (lot, block, subdivision)'
  },

  'county': {
    fieldId: 'county',
    synonyms: ['county', 'county of', 'county name'],
    type: 'string',
    category: 'property',
    wizardStep: 'project_site',
    defaultValue: 'Duval',
    dataPathPrefix: 'job',
    displayLabel: 'County'
  },

  'cityId': {
    fieldId: 'cityId',
    synonyms: ['city id', 'city number', 'municipal id'],
    type: 'string',
    category: 'property',
    wizardStep: 'project_site',
    dataPathPrefix: 'job',
    displayLabel: 'City ID'
  },

  'lotNumber': {
    fieldId: 'lotNumber',
    synonyms: ['lot', 'lot no', 'lot number', 'lot #'],
    type: 'string',
    category: 'property',
    wizardStep: 'project_site',
    dataPathPrefix: 'job',
    displayLabel: 'Lot Number'
  },

  'blockNumber': {
    fieldId: 'blockNumber',
    synonyms: ['block', 'block no', 'block number', 'block #'],
    type: 'string',
    category: 'property',
    wizardStep: 'project_site',
    dataPathPrefix: 'job',
    displayLabel: 'Block Number'
  },

  'subdivision': {
    fieldId: 'subdivision',
    synonyms: ['subdivision', 'subdivision name', 'plat name', 'development name'],
    type: 'string',
    category: 'property',
    wizardStep: 'project_site',
    dataPathPrefix: 'job',
    displayLabel: 'Subdivision'
  },

  // ===========================================================================
  // OWNER INFORMATION
  // ===========================================================================

  'ownerName': {
    fieldId: 'ownerName',
    synonyms: [
      'owner', 'owner name', 'property owner', 'owner\'s name',
      'name of owner', 'owner/applicant', 'property owner name',
      'print name', 'printed name of property owner'
    ],
    type: 'string',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    commonlyRequired: true,
    dataPathPrefix: 'job',
    displayLabel: 'Property Owner Name',
    description: 'Full legal name of property owner'
  },

  'ownerAddress': {
    fieldId: 'ownerAddress',
    synonyms: [
      'owner address', 'owner\'s address', 'owner mailing address',
      'mailing address', 'property owner address'
    ],
    type: 'address',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'job',
    displayLabel: 'Owner Mailing Address'
  },

  'ownerPhone': {
    fieldId: 'ownerPhone',
    synonyms: [
      'owner phone', 'owner phone number', 'owner phone#', 'owner tel',
      'property owner phone', 'phone', 'telephone'
    ],
    type: 'phone',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    transform: 'phone_format',
    dataPathPrefix: 'job',
    displayLabel: 'Owner Phone'
  },

  'ownerCell': {
    fieldId: 'ownerCell',
    synonyms: ['owner cell', 'owner mobile', 'cell phone', 'mobile phone'],
    type: 'phone',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    transform: 'phone_format',
    dataPathPrefix: 'job',
    displayLabel: 'Owner Cell Phone'
  },

  'ownerEmail': {
    fieldId: 'ownerEmail',
    synonyms: ['owner email', 'email', 'e-mail', 'owner e-mail'],
    type: 'email',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'job',
    displayLabel: 'Owner Email'
  },

  'ownerInterest': {
    fieldId: 'ownerInterest',
    synonyms: [
      'owner\'s interest', 'ownership interest', 'interest in property',
      'owner interest in site'
    ],
    type: 'enum',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    defaultValue: 'Fee Simple',
    dataPathPrefix: 'job',
    displayLabel: 'Owner\'s Interest in Property'
  },

  'ownerInitials': {
    fieldId: 'ownerInitials',
    synonyms: ['initial', 'initials', 'owner initials'],
    type: 'initials',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'job',
    displayLabel: 'Owner Initials'
  },

  'homesteadExemption': {
    fieldId: 'homesteadExemption',
    synonyms: ['homestead', 'homestead exemption', 'homestead exempt'],
    type: 'boolean',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'job',
    displayLabel: 'Homestead Exemption'
  },

  // ===========================================================================
  // CONTRACTOR INFORMATION
  // ===========================================================================

  'companyName': {
    fieldId: 'companyName',
    synonyms: [
      'company name', 'contractor', 'contractor name', 'business name',
      'contractor company name', 'contractor business name', 'firm name',
      'company', 'contracting company'
    ],
    type: 'string',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    commonlyRequired: true,
    dataPathPrefix: 'contractor',
    displayLabel: 'Contractor Company Name'
  },

  'qualifierName': {
    fieldId: 'qualifierName',
    synonyms: [
      'qualifier', 'qualifier name', 'qualifier\'s name', 'licensed qualifier',
      'qualifying agent', 'agent name'
    ],
    type: 'string',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    commonlyRequired: true,
    dataPathPrefix: 'contractor',
    displayLabel: 'Qualifier Name',
    description: 'Name of the licensed qualifying agent'
  },

  'licenseNumber': {
    fieldId: 'licenseNumber',
    synonyms: [
      'license', 'license number', 'license no', 'license #',
      'contractor license', 'contractor license number', 'lic no',
      'state license', 'qualifier\'s license number', 'qualifiers license number'
    ],
    type: 'string',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    commonlyRequired: true,
    dataPathPrefix: 'contractor',
    displayLabel: 'License Number'
  },

  'companyAddress': {
    fieldId: 'companyAddress',
    synonyms: [
      'contractor address', 'company address', 'business address',
      'contractor mailing address'
    ],
    type: 'address',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Contractor Address'
  },

  'companyPhone': {
    fieldId: 'companyPhone',
    synonyms: [
      'contractor phone', 'company phone', 'business phone',
      'contractor phone number', 'phone no', 'phone#'
    ],
    type: 'phone',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    transform: 'phone_format',
    dataPathPrefix: 'contractor',
    displayLabel: 'Contractor Phone'
  },

  'companyFax': {
    fieldId: 'companyFax',
    synonyms: ['contractor fax', 'company fax', 'fax', 'fax no', 'fax number'],
    type: 'phone',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Contractor Fax'
  },

  'companyEmail': {
    fieldId: 'companyEmail',
    synonyms: ['contractor email', 'company email', 'business email'],
    type: 'email',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Contractor Email'
  },

  'primaryContactName': {
    fieldId: 'primaryContactName',
    synonyms: ['primary contact', 'contact name', 'primary contact name'],
    type: 'string',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Primary Contact Name'
  },

  'primaryContactPhone': {
    fieldId: 'primaryContactPhone',
    synonyms: ['primary contact phone', 'contact phone'],
    type: 'phone',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    transform: 'phone_format',
    dataPathPrefix: 'contractor',
    displayLabel: 'Primary Contact Phone'
  },

  'primaryContactEmail': {
    fieldId: 'primaryContactEmail',
    synonyms: ['primary contact email', 'contact email'],
    type: 'email',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Primary Contact Email'
  },

  'secondaryContactName': {
    fieldId: 'secondaryContactName',
    synonyms: ['secondary contact', 'alternate contact', 'secondary contact name'],
    type: 'string',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Secondary Contact Name'
  },

  'secondaryContactPhone': {
    fieldId: 'secondaryContactPhone',
    synonyms: ['secondary contact phone', 'alternate contact phone'],
    type: 'phone',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    transform: 'phone_format',
    dataPathPrefix: 'contractor',
    displayLabel: 'Secondary Contact Phone'
  },

  'secondaryContactEmail': {
    fieldId: 'secondaryContactEmail',
    synonyms: ['secondary contact email', 'alternate contact email'],
    type: 'email',
    category: 'contractor',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Secondary Contact Email'
  },

  // ===========================================================================
  // PROJECT / PERMIT INFORMATION
  // ===========================================================================

  'permitNumber': {
    fieldId: 'permitNumber',
    synonyms: [
      'permit', 'permit number', 'permit no', 'permit #',
      'application number', 'app no', 'permit id'
    ],
    type: 'string',
    category: 'permit',
    wizardStep: 'scope_permit_type',
    dataPathPrefix: 'job',
    displayLabel: 'Permit Number',
    description: 'Assigned permit number (if known)'
  },

  'scopeOfWork': {
    fieldId: 'scopeOfWork',
    synonyms: [
      'scope of work', 'description of work', 'work description',
      'general description', 'description of improvements',
      'general description of improvements', 'project description',
      'nature of work', 'type of work'
    ],
    type: 'text',
    category: 'project',
    wizardStep: 'scope_permit_type',
    transform: 'truncate80',
    commonlyRequired: true,
    dataPathPrefix: 'job',
    displayLabel: 'Description of Work',
    description: 'Brief description of the work to be performed'
  },

  'estimatedValue': {
    fieldId: 'estimatedValue',
    synonyms: [
      'estimated value', 'job value', 'project value', 'construction value',
      'total value', 'value of work', 'cost', 'estimated cost',
      'valuation', 'construction cost'
    ],
    type: 'currency',
    category: 'project',
    wizardStep: 'job_details',
    transform: 'currency',
    commonlyRequired: true,
    dataPathPrefix: 'job',
    displayLabel: 'Estimated Value',
    description: 'Total estimated cost of construction'
  },

  'squareFootage': {
    fieldId: 'squareFootage',
    synonyms: [
      'square footage', 'sq ft', 'sqft', 'square feet', 'area',
      'total area', 'building area', 'heated area', 'conditioned area'
    ],
    type: 'number',
    category: 'project',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'Square Footage'
  },

  'stories': {
    fieldId: 'stories',
    synonyms: ['stories', 'number of stories', 'floors', 'number of floors'],
    type: 'number',
    category: 'project',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'Number of Stories'
  },

  'constructionType': {
    fieldId: 'constructionType',
    synonyms: [
      'construction type', 'type of construction', 'building type',
      'occupancy type', 'use type'
    ],
    type: 'enum',
    category: 'project',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'Construction Type'
  },

  'newOrExisting': {
    fieldId: 'newOrExisting',
    synonyms: [
      'new or existing', 'new construction', 'existing structure',
      'addition', 'alteration', 'renovation'
    ],
    type: 'enum',
    category: 'project',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'New or Existing'
  },

  // ===========================================================================
  // SURETY / BONDING INFORMATION
  // ===========================================================================

  'bondingCompany': {
    fieldId: 'bondingCompany',
    synonyms: [
      'surety', 'bonding company', 'bond company', 'surety company',
      'surety name', 'insurance company'
    ],
    type: 'string',
    category: 'surety',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Bonding/Surety Company'
  },

  'bondAmount': {
    fieldId: 'bondAmount',
    synonyms: ['bond amount', 'amount of bond', 'surety amount', 'bond value'],
    type: 'currency',
    category: 'surety',
    wizardStep: 'applicant_contractor',
    transform: 'currency',
    dataPathPrefix: 'contractor',
    displayLabel: 'Bond Amount'
  },

  'bondingCompanyPhone': {
    fieldId: 'bondingCompanyPhone',
    synonyms: ['surety phone', 'bond company phone', 'bonding company phone'],
    type: 'phone',
    category: 'surety',
    wizardStep: 'applicant_contractor',
    transform: 'phone_format',
    dataPathPrefix: 'contractor',
    displayLabel: 'Surety Phone'
  },

  'bondingCompanyAddress': {
    fieldId: 'bondingCompanyAddress',
    synonyms: ['surety address', 'bond company address', 'bonding company address'],
    type: 'address',
    category: 'surety',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'contractor',
    displayLabel: 'Surety Address'
  },

  // ===========================================================================
  // LENDER INFORMATION
  // ===========================================================================

  'lenderName': {
    fieldId: 'lenderName',
    synonyms: ['lender', 'lender name', 'lending institution', 'bank', 'mortgage company'],
    type: 'string',
    category: 'lender',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'Lender Name'
  },

  'lenderAddress': {
    fieldId: 'lenderAddress',
    synonyms: ['lender address', 'bank address'],
    type: 'address',
    category: 'lender',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'Lender Address'
  },

  'lenderPhone': {
    fieldId: 'lenderPhone',
    synonyms: ['lender phone', 'bank phone'],
    type: 'phone',
    category: 'lender',
    wizardStep: 'job_details',
    transform: 'phone_format',
    dataPathPrefix: 'job',
    displayLabel: 'Lender Phone'
  },

  // ===========================================================================
  // DATES
  // ===========================================================================

  'currentDate': {
    fieldId: 'currentDate',
    synonyms: ['date', 'today\'s date', 'current date', 'application date'],
    type: 'date',
    category: 'dates',
    wizardStep: 'review_export',
    transform: 'date_format',
    dataPathPrefix: 'generated',
    displayLabel: 'Date'
  },

  'currentMonth': {
    fieldId: 'currentMonth',
    synonyms: ['month', 'month number'],
    type: 'string',
    category: 'dates',
    wizardStep: 'review_export',
    dataPathPrefix: 'generated',
    displayLabel: 'Month'
  },

  'currentDay': {
    fieldId: 'currentDay',
    synonyms: ['day', 'day number'],
    type: 'string',
    category: 'dates',
    wizardStep: 'review_export',
    dataPathPrefix: 'generated',
    displayLabel: 'Day'
  },

  'currentYear': {
    fieldId: 'currentYear',
    synonyms: ['year', 'year number'],
    type: 'string',
    category: 'dates',
    wizardStep: 'review_export',
    dataPathPrefix: 'generated',
    displayLabel: 'Year'
  },

  'currentYearShort': {
    fieldId: 'currentYearShort',
    synonyms: ['year last 2 digits', 'year 2 digits', 'yy'],
    type: 'string',
    category: 'dates',
    wizardStep: 'review_export',
    dataPathPrefix: 'generated',
    displayLabel: 'Year (2 digits)'
  },

  'expirationDate': {
    fieldId: 'expirationDate',
    synonyms: ['expiration date', 'expires', 'expiry date', 'valid until'],
    type: 'date',
    category: 'dates',
    wizardStep: 'review_export',
    transform: 'date_format',
    dataPathPrefix: 'job',
    displayLabel: 'Expiration Date'
  },

  // ===========================================================================
  // NOTARY FIELDS
  // ===========================================================================

  'notaryState': {
    fieldId: 'notaryState',
    synonyms: ['notary state', 'state of', 'notary public state'],
    type: 'string',
    category: 'notary',
    wizardStep: 'review_export',
    defaultValue: 'Florida',
    dataPathPrefix: 'job',
    displayLabel: 'Notary State'
  },

  'notaryCounty': {
    fieldId: 'notaryCounty',
    synonyms: ['notary county', 'county of'],
    type: 'string',
    category: 'notary',
    wizardStep: 'review_export',
    defaultValue: 'Duval',
    dataPathPrefix: 'job',
    displayLabel: 'Notary County'
  },

  'personallyAppeared': {
    fieldId: 'personallyAppeared',
    synonyms: ['personally appeared', 'affiant', 'appeared before me'],
    type: 'string',
    category: 'notary',
    wizardStep: 'review_export',
    dataPathPrefix: 'job',
    displayLabel: 'Personally Appeared (Affiant)'
  },

  'commissionExpires': {
    fieldId: 'commissionExpires',
    synonyms: [
      'commission expires', 'my commission expires',
      'notary commission expires', 'comm expires'
    ],
    type: 'date',
    category: 'notary',
    wizardStep: 'review_export',
    dataPathPrefix: 'job',
    displayLabel: 'Commission Expires'
  },

  'idType': {
    fieldId: 'idType',
    synonyms: [
      'type of id', 'type of id produced', 'identification type',
      'type of identification', 'id produced'
    ],
    type: 'string',
    category: 'notary',
    wizardStep: 'review_export',
    dataPathPrefix: 'job',
    displayLabel: 'Type of ID Produced'
  },

  'personallyKnown': {
    fieldId: 'personallyKnown',
    synonyms: ['personally known', 'known to me'],
    type: 'boolean',
    category: 'notary',
    wizardStep: 'review_export',
    dataPathPrefix: 'job',
    displayLabel: 'Personally Known'
  },

  // ===========================================================================
  // SIGNATURES
  // ===========================================================================

  'ownerSignature': {
    fieldId: 'ownerSignature',
    synonyms: [
      'owner signature', 'property owner signature', 'signature of owner',
      'signature of property owner', 'owner/agent signature'
    ],
    type: 'signature',
    category: 'signatures',
    wizardStep: 'review_export',
    dataPathPrefix: 'job',
    displayLabel: 'Owner Signature'
  },

  'contractorSignature': {
    fieldId: 'contractorSignature',
    synonyms: [
      'contractor signature', 'qualifier signature',
      'contractor/qualifier signature'
    ],
    type: 'signature',
    category: 'signatures',
    wizardStep: 'review_export',
    dataPathPrefix: 'contractor',
    displayLabel: 'Contractor Signature'
  },

  'notarySignature': {
    fieldId: 'notarySignature',
    synonyms: ['notary signature', 'notary public signature', 'notary public'],
    type: 'signature',
    category: 'signatures',
    wizardStep: 'review_export',
    dataPathPrefix: 'job',
    displayLabel: 'Notary Signature'
  },

  'notaryPrintedName': {
    fieldId: 'notaryPrintedName',
    synonyms: ['notary printed name', 'notary name', 'printed name'],
    type: 'string',
    category: 'notary',
    wizardStep: 'review_export',
    dataPathPrefix: 'job',
    displayLabel: 'Notary Printed Name'
  },

  // ===========================================================================
  // POOL-SPECIFIC FIELDS
  // ===========================================================================

  'poolBarrierType': {
    fieldId: 'poolBarrierType',
    synonyms: ['pool barrier', 'barrier type', 'pool safety barrier'],
    type: 'enum',
    category: 'pool',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'Pool Barrier Type'
  },

  'poolSafetyCover': {
    fieldId: 'poolSafetyCover',
    synonyms: ['safety pool cover', 'pool cover', 'astm safety cover'],
    type: 'boolean',
    category: 'pool',
    wizardStep: 'job_details',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'ASTM Safety Pool Cover'
  },

  'poolScreenEnclosure': {
    fieldId: 'poolScreenEnclosure',
    synonyms: ['screen enclosure', 'pool enclosure', 'screened enclosure'],
    type: 'boolean',
    category: 'pool',
    wizardStep: 'job_details',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'Screen Enclosure'
  },

  'poolFenceGates': {
    fieldId: 'poolFenceGates',
    synonyms: ['fence and gates', 'pool fence', 'pedestrian gates'],
    type: 'boolean',
    category: 'pool',
    wizardStep: 'job_details',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'Fence and Pedestrian Gates'
  },

  'poolExitAlarm': {
    fieldId: 'poolExitAlarm',
    synonyms: ['exit alarm', 'door alarm', 'pool exit alarm'],
    type: 'boolean',
    category: 'pool',
    wizardStep: 'job_details',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'Exit Alarm'
  },

  'poolAlarm': {
    fieldId: 'poolAlarm',
    synonyms: ['pool alarm', 'swimming pool alarm', 'water alarm'],
    type: 'boolean',
    category: 'pool',
    wizardStep: 'job_details',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'Pool Alarm'
  },

  // ===========================================================================
  // PRIVATE PROVIDER FIELDS
  // ===========================================================================

  'privateProviderPlanReview': {
    fieldId: 'privateProviderPlanReview',
    synonyms: ['plan review', 'ppi plan review', 'private provider plan review'],
    type: 'boolean',
    category: 'private_provider',
    wizardStep: 'scope_permit_type',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'Private Provider Plan Review'
  },

  'privateProviderInspections': {
    fieldId: 'privateProviderInspections',
    synonyms: ['inspections', 'ppi inspections', 'private provider inspections'],
    type: 'boolean',
    category: 'private_provider',
    wizardStep: 'scope_permit_type',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'Private Provider Inspections'
  },

  // ===========================================================================
  // TCO/PCO/PST FIELDS
  // ===========================================================================

  'tcoRequested': {
    fieldId: 'tcoRequested',
    synonyms: ['tco', 'temporary certificate of occupancy', 'tco requested'],
    type: 'boolean',
    category: 'permit',
    wizardStep: 'scope_permit_type',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'TCO Requested'
  },

  'pcoRequested': {
    fieldId: 'pcoRequested',
    synonyms: ['pco', 'partial certificate of occupancy', 'pco requested'],
    type: 'boolean',
    category: 'permit',
    wizardStep: 'scope_permit_type',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'PCO Requested'
  },

  'pstRequested': {
    fieldId: 'pstRequested',
    synonyms: ['pst', 'permission to stock', 'permission to stock and train'],
    type: 'boolean',
    category: 'permit',
    wizardStep: 'scope_permit_type',
    transform: 'checkbox',
    dataPathPrefix: 'job',
    displayLabel: 'PST Requested'
  },

  'tcoReason': {
    fieldId: 'tcoReason',
    synonyms: ['reason for request', 'tco reason', 'request reason'],
    type: 'text',
    category: 'permit',
    wizardStep: 'scope_permit_type',
    transform: 'truncate80',
    dataPathPrefix: 'job',
    displayLabel: 'Reason for Request'
  },

  'outstandingIssues': {
    fieldId: 'outstandingIssues',
    synonyms: ['outstanding issues', 'pending items', 'remaining work'],
    type: 'text',
    category: 'permit',
    wizardStep: 'scope_permit_type',
    transform: 'truncate80',
    dataPathPrefix: 'job',
    displayLabel: 'Outstanding Issues'
  },

  'associatedPermits': {
    fieldId: 'associatedPermits',
    synonyms: ['associated permits', 'related permits', 'outstanding permits'],
    type: 'string',
    category: 'permit',
    wizardStep: 'scope_permit_type',
    dataPathPrefix: 'job',
    displayLabel: 'Associated Permits'
  },

  // ===========================================================================
  // FEE SIMPLE / TITLE HOLDERS
  // ===========================================================================

  'feeSimpleHolder': {
    fieldId: 'feeSimpleHolder',
    synonyms: ['fee simple holder', 'title holder', 'fee holder'],
    type: 'string',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'job',
    displayLabel: 'Fee Simple Holder'
  },

  'feeSimpleHolderName': {
    fieldId: 'feeSimpleHolderName',
    synonyms: ['fee simple holder name', 'title holder name'],
    type: 'string',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'job',
    displayLabel: 'Fee Simple Holder Name'
  },

  'feeSimpleHolderAddress': {
    fieldId: 'feeSimpleHolderAddress',
    synonyms: ['fee simple holder address', 'title holder address'],
    type: 'address',
    category: 'owner',
    wizardStep: 'applicant_contractor',
    dataPathPrefix: 'job',
    displayLabel: 'Fee Simple Holder Address'
  },

  // ===========================================================================
  // DESIGN PROFESSIONAL
  // ===========================================================================

  'designProfessionalName': {
    fieldId: 'designProfessionalName',
    synonyms: [
      'design professional', 'architect', 'engineer', 'designer',
      'professional engineer', 'pe', 'registered architect'
    ],
    type: 'string',
    category: 'project',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'Design Professional Name'
  },

  'designProfessionalPhone': {
    fieldId: 'designProfessionalPhone',
    synonyms: ['design professional phone', 'architect phone', 'engineer phone'],
    type: 'phone',
    category: 'project',
    wizardStep: 'job_details',
    transform: 'phone_format',
    dataPathPrefix: 'job',
    displayLabel: 'Design Professional Phone'
  },

  'designProfessionalLicense': {
    fieldId: 'designProfessionalLicense',
    synonyms: ['pe license', 'architect license', 'design professional license'],
    type: 'string',
    category: 'project',
    wizardStep: 'job_details',
    dataPathPrefix: 'job',
    displayLabel: 'Design Professional License'
  }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Find a canonical field by matching against synonyms (case-insensitive)
 */
export function findFieldBySynonym(label: string): OntologyField | null {
  const normalizedLabel = label.toLowerCase().trim();

  for (const field of Object.values(FIELD_ONTOLOGY)) {
    // Check exact match on fieldId
    if (field.fieldId.toLowerCase() === normalizedLabel) {
      return field;
    }

    // Check synonyms
    for (const synonym of field.synonyms) {
      if (synonym.toLowerCase() === normalizedLabel) {
        return field;
      }

      // Also check if the label contains the synonym
      if (normalizedLabel.includes(synonym.toLowerCase())) {
        return field;
      }
    }
  }

  return null;
}

/**
 * Get all fields for a specific wizard step
 */
export function getFieldsForWizardStep(step: WizardStep): OntologyField[] {
  return Object.values(FIELD_ONTOLOGY).filter(f => f.wizardStep === step);
}

/**
 * Get all fields for a specific category
 */
export function getFieldsForCategory(category: FieldCategory): OntologyField[] {
  return Object.values(FIELD_ONTOLOGY).filter(f => f.category === category);
}

/**
 * Build the full dataPath from an ontology field
 */
export function buildDataPath(field: OntologyField): string {
  return `${field.dataPathPrefix}.${field.fieldId}`;
}

/**
 * Calculate confidence score for a field match
 */
export function calculateMatchConfidence(label: string, field: OntologyField): number {
  const normalizedLabel = label.toLowerCase().trim();

  // Exact match on fieldId = 1.0
  if (field.fieldId.toLowerCase() === normalizedLabel) {
    return 1.0;
  }

  // Exact synonym match = 0.95
  for (const synonym of field.synonyms) {
    if (synonym.toLowerCase() === normalizedLabel) {
      return 0.95;
    }
  }

  // Label contains synonym = 0.8
  for (const synonym of field.synonyms) {
    if (normalizedLabel.includes(synonym.toLowerCase())) {
      return 0.8;
    }
  }

  // Synonym contains label = 0.7
  for (const synonym of field.synonyms) {
    if (synonym.toLowerCase().includes(normalizedLabel)) {
      return 0.7;
    }
  }

  return 0;
}

/**
 * Get commonly required fields
 */
export function getRequiredFields(): OntologyField[] {
  return Object.values(FIELD_ONTOLOGY).filter(f => f.commonlyRequired);
}

// =============================================================================
// WIZARD STEP DEFINITIONS
// =============================================================================

export const WIZARD_STEPS: Array<{
  id: WizardStep;
  title: string;
  description: string;
  categories: FieldCategory[];
}> = [
  {
    id: 'project_site',
    title: 'Project & Site',
    description: 'Property location and details',
    categories: ['property']
  },
  {
    id: 'applicant_contractor',
    title: 'Applicant & Contractor',
    description: 'Owner and contractor information',
    categories: ['owner', 'contractor', 'surety']
  },
  {
    id: 'scope_permit_type',
    title: 'Scope & Permit Type',
    description: 'What work is being done',
    categories: ['permit', 'private_provider']
  },
  {
    id: 'job_details',
    title: 'Job Details',
    description: 'Valuation, specifications, and trade details',
    categories: ['project', 'lender', 'pool', 'electrical', 'mechanical', 'plumbing', 'roofing']
  },
  {
    id: 'documents',
    title: 'Documents & Attachments',
    description: 'Required documents and uploads',
    categories: []
  },
  {
    id: 'review_export',
    title: 'Review & Export',
    description: 'Review all information and generate forms',
    categories: ['dates', 'notary', 'signatures']
  }
];

// =============================================================================
// SEMANTIC FIELD ID (Phase 6 Compatibility)
// =============================================================================

/**
 * Union type of all canonical field IDs from the ontology.
 * Use this for type-safe field references.
 */
export type SemanticFieldId = keyof typeof FIELD_ONTOLOGY;

/**
 * Get all valid semantic field IDs as an array.
 */
export function getAllSemanticFieldIds(): SemanticFieldId[] {
  return Object.keys(FIELD_ONTOLOGY) as SemanticFieldId[];
}

/**
 * Check if a string is a valid semantic field ID.
 */
export function isValidSemanticFieldId(id: string): id is SemanticFieldId {
  return id in FIELD_ONTOLOGY;
}

/**
 * Get an ontology field by its semantic ID (type-safe).
 */
export function getOntologyField(id: SemanticFieldId): OntologyField {
  return FIELD_ONTOLOGY[id];
}

// =============================================================================
// DATA PATH RESOLUTION
// =============================================================================

/**
 * Data paths tried in order for each semantic field.
 * First match wins. Used for cross-form data portability.
 */
export interface DataPathMapping {
  /** Primary data paths to try (first match wins) */
  dataPaths: string[];
  /** Transform to apply to resolved value */
  transform?: string;
  /** Default value if no data found */
  defaultValue?: string;
  /** Whether this is a generated/computed value */
  generated?: boolean;
}

/**
 * Build data path mappings from the ontology.
 * This enables "fill once, use everywhere" across forms.
 */
export function getDataPathMapping(fieldId: SemanticFieldId): DataPathMapping {
  const field = FIELD_ONTOLOGY[fieldId];
  const primaryPath = `${field.dataPathPrefix}.${field.fieldId}`;

  // Build alternative paths based on common patterns
  const altPaths: string[] = [];

  // Add common alternative paths
  if (field.dataPathPrefix === 'job') {
    altPaths.push(`job.${field.fieldId}`);
    // Common variations
    if (field.category === 'owner') {
      altPaths.push(`job.client.${field.fieldId.replace('owner', '')}`);
      altPaths.push(`job.propertyOwner${field.fieldId.replace('owner', '')}`);
    }
  } else if (field.dataPathPrefix === 'contractor') {
    altPaths.push(`contractor.${field.fieldId}`);
    altPaths.push(`contractor.profile.${field.fieldId}`);
  }

  return {
    dataPaths: [primaryPath, ...altPaths],
    transform: field.transform,
    defaultValue: field.defaultValue,
    generated: field.dataPathPrefix === 'generated'
  };
}

/**
 * Get all data path mappings indexed by field ID.
 */
export function getAllDataPathMappings(): Record<SemanticFieldId, DataPathMapping> {
  const mappings: Partial<Record<SemanticFieldId, DataPathMapping>> = {};
  for (const fieldId of getAllSemanticFieldIds()) {
    mappings[fieldId] = getDataPathMapping(fieldId);
  }
  return mappings as Record<SemanticFieldId, DataPathMapping>;
}
